import { useMemo, useEffect, useRef } from 'react';
import config from '../../../../reducers/config';
import { hasAcceptableHostName } from '../../../../utils/url';
import storageFactory from '../../../../utils/storageFactory';
import { ltLogger } from '../../../../bootstrap';
import * as logtypes from '../../../../common/logtypes';

const storage = storageFactory();

// PHX-7746: persisted file fields, excluding `progress` (UI-only, changes on
// every upload tick). Keying the session update on this signature instead of
// the whole acceptedFiles array stops one upload from firing many parallel
// POST /session/update/FileUpload requests on a single session.
const SIGNATURE_FIELDS = [
  'id',
  'name',
  'size',
  'type',
  'complete',
  'failed',
  'rejected',
  'fileUid',
  'fileUrl',
];

const getFilesSignature = (files) => {
  if (!Array.isArray(files)) return '';
  return JSON.stringify(
    files.map((file) =>
      file && typeof file === 'object'
        ? SIGNATURE_FIELDS.map((key) => file[key])
        : file
    )
  );
};

// Coalesce rapid session updates (e.g. several files completing within a few
// hundred ms of each other) into a single request.
const DEBOUNCE_MS = 400;

export default function useUpdateSession(files, formId, attributeId) {
  const url = useMemo(() => {
    const baseUrl = `${config.apiConfig.rootApiServerPath}${config.apiConfig.updateSessionUrlPath}${attributeId}`;
    const queryParams = formId ? `?formId=${encodeURIComponent(formId)}` : '';
    return `${baseUrl}${queryParams}`;
  }, [attributeId, formId]);

  const signature = useMemo(() => getFilesSignature(files), [files]);

  // Read the freshest file list at send time without re-running on every tick.
  const filesRef = useRef(files);
  filesRef.current = files;

  const lastSentSignature = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (signature === lastSentSignature.current) {
      return undefined;
    }

    const hasFiles = Array.isArray(files) && files.length > 0;
    // Skip the empty write on mount, but still allow a later N -> 0 clear.
    if (!hasFiles && lastSentSignature.current === null) {
      lastSentSignature.current = signature;
      return undefined;
    }

    const makeRequest = async () => {
      const acceptableHostName = hasAcceptableHostName(url);
      const request = new Request(url, {
        method: 'POST',
        body: JSON.stringify(filesRef.current),
        credentials: acceptableHostName ? 'include' : 'omit',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json',
          'x-lt-phoenix': storage.getItem('lt_sessionKey'),
        },
      });

      try {
        const response = await fetch(request);

        if (response.status >= 400) {
          ltLogger(logtypes.ERROR, undefined, {}, undefined, {
            error: new Error(`Error Updating Session for ${attributeId}`),
          });
        }
      } catch (err) {
        ltLogger(logtypes.ERROR, undefined, {}, undefined, {
          error: err,
          metaData: { formId },
        });
      }
    };

    timerRef.current = setTimeout(() => {
      lastSentSignature.current = signature;
      makeRequest();
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [signature, files, formId, attributeId, url]);
}
